/* ============================================
   HOME AUTOMATION DESIGNS — SHOPIFY THEME JS
   ============================================ */

(function () {
  'use strict';

  // ============================================
  // UTILITIES
  // ============================================
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];
  const on = (el, ev, fn) => el && el.addEventListener(ev, fn);
  const off = (el, ev, fn) => el && el.removeEventListener(ev, fn);
  const formatMoney = (cents) => {
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: window.Shopify?.currency?.active || 'CAD' }).format(cents / 100);
  };

  // ============================================
  // HEADER SCROLL (+ overlay mode)
  // ============================================
  const header = $('.site-header');
  if (header) {
    const isOverlay = header.dataset.overlay === 'true';
    const announcementBar = $('.announcement-bar');

    // Measure announcement bar and push header + main-content down accordingly
    const updateOffsets = () => {
      const barH = announcementBar ? announcementBar.offsetHeight : 0;
      document.documentElement.style.setProperty('--announcement-bar-height', barH + 'px');
    };
    updateOffsets();
    on(window, 'resize', updateOffsets);

    // Scroll threshold: snap to solid after hero title is hidden
    const threshold = isOverlay ? 80 : 50;
    const onScroll = () => {
      header.classList.toggle('scrolled', window.scrollY > threshold);
    };
    on(window, 'scroll', onScroll);
    onScroll();
  }

  // ============================================
  // MOBILE MENU
  // ============================================
  const mobileMenu = $('.mobile-menu');
  const mobileMenuBtn = $('.mobile-menu-btn');
  const mobileMenuClose = $('.mobile-menu__close');
  const overlay = $('#overlay');

  const openMobileMenu = () => {
    mobileMenu?.classList.add('is-open');
    overlay?.classList.add('is-active');
    document.body.style.overflow = 'hidden';
    mobileMenuBtn?.setAttribute('aria-expanded', 'true');
  };

  const closeMobileMenu = () => {
    mobileMenu?.classList.remove('is-open');
    overlay?.classList.remove('is-active');
    document.body.style.overflow = '';
    mobileMenuBtn?.setAttribute('aria-expanded', 'false');
  };

  on(mobileMenuBtn, 'click', openMobileMenu);
  on(mobileMenuClose, 'click', closeMobileMenu);

  // Mobile nav accordion (mega menu + regular dropdowns)
  $$('.mobile-nav-toggle').forEach((btn) => {
    on(btn, 'click', () => {
      const item = btn.closest('.mobile-nav-item');
      if (!item) return;
      const isOpen = item.classList.toggle('is-open');
      btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    });
  });

  // Mobile mega menu column headings (nested accordion within a mega menu item)
  $$('.mobile-nav-sub__toggle').forEach((btn) => {
    on(btn, 'click', () => {
      const group = btn.closest('.mobile-nav-sub__group');
      if (!group) return;
      const isOpen = group.classList.toggle('is-open');
      btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    });
  });

  // ============================================
  // SEARCH DRAWER
  // ============================================
  const searchDrawer = $('.search-drawer');
  const searchToggleBtns = $$('[data-search-toggle]');
  const searchCloseBtn = $('.search-drawer__close');

  const openSearch = () => {
    searchDrawer?.classList.add('is-open');
    overlay?.classList.add('is-active');
    setTimeout(() => $('.search-input', searchDrawer)?.focus(), 100);
  };

  const closeSearch = () => {
    searchDrawer?.classList.remove('is-open');
    overlay?.classList.remove('is-active');
  };

  searchToggleBtns.forEach(btn => on(btn, 'click', openSearch));
  on(searchCloseBtn, 'click', closeSearch);

  // ============================================
  // CART DRAWER
  // ============================================
  const cartDrawer = $('.cart-drawer');
  const cartToggleBtns = $$('[data-cart-toggle]');
  const cartCloseBtn = $('.cart-drawer__close');

  const openCartDrawer = () => {
    cartDrawer?.classList.add('is-open');
    overlay?.classList.add('is-active');
    document.body.style.overflow = 'hidden';
    fetchCart();
  };

  const closeCartDrawer = () => {
    cartDrawer?.classList.remove('is-open');
    overlay?.classList.remove('is-active');
    document.body.style.overflow = '';
  };

  cartToggleBtns.forEach(btn => on(btn, 'click', openCartDrawer));
  on(cartCloseBtn, 'click', closeCartDrawer);

  // Overlay click closes all
  on(overlay, 'click', () => {
    closeCartDrawer();
    closeSearch();
    closeMobileMenu();
  });

  // Escape key closes all
  on(document, 'keydown', (e) => {
    if (e.key === 'Escape') {
      closeCartDrawer();
      closeSearch();
      closeMobileMenu();
    }
  });

  // ============================================
  // CART FETCH & UPDATE
  // ============================================
  const cartCountEl = $$('.cart-count');
  const cartBodyEl = $('.cart-drawer__body');
  const cartFooterEl = $('.cart-drawer__footer');
  const cartSubtotalEl = $('.cart-subtotal__price');

  const updateCartCount = (count) => {
    cartCountEl.forEach(el => {
      el.textContent = count;
      el.dataset.count = count;
      el.style.display = count > 0 ? 'flex' : 'none';
    });
  };

  const fetchCart = async () => {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      updateCartCount(cart.item_count);
      renderCartDrawer(cart);
    } catch (e) {
      console.error('Cart fetch error:', e);
    }
  };

  const renderCartDrawer = (cart) => {
    if (!cartBodyEl) return;

    if (cart.item_count === 0) {
      cartBodyEl.innerHTML = `
        <div class="cart-empty">
          <div class="cart-empty__icon">🛒</div>
          <p class="cart-empty__text">Your cart is empty</p>
          <a href="/collections/all" class="btn btn--primary">Continue Shopping</a>
        </div>`;
      cartFooterEl?.setAttribute('hidden', '');
      return;
    }

    const itemsHtml = cart.items.map(item => `
      <div class="cart-item" data-key="${item.key}">
        <div class="cart-item__image">
          <a href="${item.url}">
            <img src="${item.featured_image?.url ? item.featured_image.url + '&width=180' : ''}" alt="${item.product_title}" loading="lazy">
          </a>
        </div>
        <div class="cart-item__details">
          <div>
            <div class="cart-item__name"><a href="${item.url}">${item.product_title}</a></div>
            ${item.variant_title && item.variant_title !== 'Default Title' ? `<div class="cart-item__variant">${item.variant_title}</div>` : ''}
          </div>
          <div class="cart-item__actions">
            <div class="quantity-selector">
              <button class="quantity-btn" data-action="decrease" data-key="${item.key}" aria-label="Decrease quantity">−</button>
              <input class="quantity-input" type="number" value="${item.quantity}" min="0" data-key="${item.key}" aria-label="Quantity">
              <button class="quantity-btn" data-action="increase" data-key="${item.key}" aria-label="Increase quantity">+</button>
            </div>
            <div class="cart-item__price">
              ${item.quantity > 1 ? `<div class="cart-item__price-unit">${formatMoney(item.final_price)} each</div>` : ''}
              ${formatMoney(item.final_line_price)}
            </div>
          </div>
          <button class="remove-item" data-key="${item.key}">Remove</button>
        </div>
      </div>`).join('');

    cartBodyEl.innerHTML = itemsHtml;

    cartFooterEl?.removeAttribute('hidden');
    if (cartSubtotalEl) cartSubtotalEl.textContent = formatMoney(cart.total_price);

    // Bind quantity buttons
    $$('.quantity-btn', cartBodyEl).forEach(btn => {
      on(btn, 'click', () => {
        const key = btn.dataset.key;
        const input = cartBodyEl.querySelector(`.quantity-input[data-key="${key}"]`);
        let qty = parseInt(input?.value || 0);
        btn.dataset.action === 'increase' ? qty++ : qty--;
        updateCartItem(key, Math.max(0, qty));
      });
    });

    $$('.quantity-input', cartBodyEl).forEach(input => {
      on(input, 'change', () => updateCartItem(input.dataset.key, Math.max(0, parseInt(input.value))));
    });

    $$('.remove-item', cartBodyEl).forEach(btn => {
      on(btn, 'click', () => updateCartItem(btn.dataset.key, 0));
    });
  };

  const updateCartItem = async (key, quantity) => {
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      const cart = await res.json();
      updateCartCount(cart.item_count);
      renderCartDrawer(cart);
    } catch (e) {
      console.error('Cart update error:', e);
    }
  };

  // ============================================
  // ADD TO CART
  // ============================================
  const handleAddToCart = async (form) => {
    const btn = form.querySelector('[type="submit"]');
    const originalText = btn?.textContent;
    if (btn) { btn.disabled = true; btn.textContent = 'Adding...'; }

    try {
      const formData = new FormData(form);
      const res = await fetch('/cart/add.js', { method: 'POST', body: formData });
      const data = await res.json();

      if (data.status) throw new Error(data.description);

      await fetchCart();
      openCartDrawer();
      showToast('Added to cart', 'success');
    } catch (e) {
      showToast(e.message || 'Could not add to cart', 'error');
    } finally {
      if (btn) { btn.disabled = false; btn.textContent = originalText; }
    }
  };

  on(document, 'submit', (e) => {
    const form = e.target.closest('form[action="/cart/add"]');
    if (!form) return;
    e.preventDefault();
    handleAddToCart(form);
  });

  // ============================================
  // PRODUCT GALLERY + LIGHTBOX (unified)
  // ============================================
  const initProductGallery = () => {
    const images = window.__productImages || [];
    if (!images.length) return;

    const mainImg = document.getElementById('MainProductImage');
    const zoomBtn = document.querySelector('[data-lightbox]');
    const thumbs = $$('.product-gallery__thumb');
    const lightbox = document.getElementById('product-lightbox');
    const lightboxImg = document.getElementById('LightboxImage');
    const lightboxCounter = document.getElementById('LightboxCounter');
    const closeBtn = lightbox?.querySelector('.product-lightbox__close');
    const prevBtn = lightbox?.querySelector('.product-lightbox__prev');
    const nextBtn = lightbox?.querySelector('.product-lightbox__next');

    let currentIndex = parseInt(zoomBtn?.dataset.index, 10) || 0;

    const showImage = (index, opts = {}) => {
      if (index < 0) index = images.length - 1;
      if (index >= images.length) index = 0;
      currentIndex = index;
      const img = images[index];
      if (!img) return;

      if (mainImg) {
        // srcset/sizes take priority over src in the browser's image selection,
        // so the original responsive attributes must be cleared or they'll
        // override any src swap made here.
        mainImg.removeAttribute('srcset');
        mainImg.removeAttribute('sizes');
        mainImg.src = img.src;
        mainImg.alt = img.alt;
      }
      if (zoomBtn) zoomBtn.dataset.index = index;

      thumbs.forEach(t => t.classList.toggle('active', parseInt(t.dataset.index, 10) === index));

      if (opts.syncLightbox && lightbox?.classList.contains('is-open')) {
        if (lightboxImg) { lightboxImg.src = img.full; lightboxImg.alt = img.alt; }
        if (lightboxCounter) lightboxCounter.textContent = `${index + 1} / ${images.length}`;
      }
    };

    thumbs.forEach(thumb => {
      on(thumb, 'click', () => {
        showImage(parseInt(thumb.dataset.index, 10), { syncLightbox: true });
      });
    });

    const openLightbox = () => {
      if (!lightbox) return;
      const img = images[currentIndex];
      if (lightboxImg && img) { lightboxImg.src = img.full; lightboxImg.alt = img.alt; }
      if (lightboxCounter) lightboxCounter.textContent = `${currentIndex + 1} / ${images.length}`;
      lightbox.classList.add('is-open');
      lightbox.removeAttribute('aria-hidden');
      document.body.style.overflow = 'hidden';
    };

    const closeLightbox = () => {
      if (!lightbox) return;
      lightbox.classList.remove('is-open');
      lightbox.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    };

    if (zoomBtn) on(zoomBtn, 'click', openLightbox);
    if (closeBtn) on(closeBtn, 'click', closeLightbox);
    if (prevBtn) on(prevBtn, 'click', () => showImage(currentIndex - 1, { syncLightbox: true }));
    if (nextBtn) on(nextBtn, 'click', () => showImage(currentIndex + 1, { syncLightbox: true }));

    if (images.length <= 1) {
      prevBtn?.setAttribute('hidden', '');
      nextBtn?.setAttribute('hidden', '');
    }

    if (lightbox) {
      on(lightbox, 'click', e => { if (e.target === lightbox) closeLightbox(); });
      document.addEventListener('keydown', e => {
        if (!lightbox.classList.contains('is-open')) return;
        if (e.key === 'Escape') closeLightbox();
        if (e.key === 'ArrowLeft') showImage(currentIndex - 1, { syncLightbox: true });
        if (e.key === 'ArrowRight') showImage(currentIndex + 1, { syncLightbox: true });
      });
    }

    // Exposed so variant selection can jump to a specific image by Shopify image id
    window.__productGallery = {
      showImageById: (id) => {
        const idx = images.findIndex(img => String(img.id) === String(id));
        if (idx > -1) showImage(idx, { syncLightbox: true });
        return idx > -1;
      }
    };
  };

  initProductGallery();

  // ============================================
  // VARIANT SELECTION
  // ============================================
  const initVariants = () => {
    const variantForm = $('.product-form');
    if (!variantForm) return;

    const variantInput = variantForm.querySelector('[name="id"]');
    const priceEl = $('.product-price');
    const comparePriceEl = $('.product-price--compare');
    const addBtn = variantForm.querySelector('[type="submit"]');
    const skuRow = $('[data-sku-row]');
    const skuValueEl = $('[data-sku-value]');
    const barcodeRow = $('[data-barcode-row]');
    const barcodeValueEl = $('[data-barcode-value]');

    if (!window.__productVariants) return;
    const variants = window.__productVariants;

    const getCurrentSelections = () => {
      const selections = {};
      $$('.variant-option-group').forEach(group => {
        const select = group.querySelector('.variant-select');
        if (select) selections[group.dataset.option] = select.value;
      });
      return selections;
    };

    const findVariant = (selections) => {
      return variants.find(v =>
        v.options.every((opt, i) => opt === selections[`option${i + 1}`])
      );
    };

    // Keep the button's original markup (e.g. the Pre-Order icon) so it can be restored
    const addBtnHTML = addBtn ? addBtn.innerHTML : '';
    const paymentButton = $('.product-info__payment-button');

    const setBuyable = (buyable, label) => {
      if (addBtn) {
        addBtn.disabled = !buyable;
        if (buyable) addBtn.innerHTML = addBtnHTML;
        else addBtn.textContent = label;
      }
      if (paymentButton) paymentButton.style.display = buyable ? '' : 'none';
    };

    // Variants whose options 1..count match the current selections
    const matchingPrefix = (selections, count) => variants.filter(v =>
      v.options.slice(0, count).every((opt, i) => opt === selections[`option${i + 1}`])
    );

    // Availability is hierarchical: option 1 is never flagged, and each later dropdown
    // only flags values that don't exist (or are sold out) under the options above it
    const updateOptionLabels = () => {
      const selections = getCurrentSelections();
      $$('.variant-select').forEach(select => {
        const index = Number(select.dataset.optionIndex);
        const parents = matchingPrefix(selections, index - 1);
        Array.from(select.options).forEach(opt => {
          const matches = parents.filter(v => v.options[index - 1] === opt.value);
          let suffix = '';
          if (!matches.length) suffix = ' — Unavailable';
          else if (!matches.some(v => v.available)) suffix = ' — Sold out';
          opt.textContent = opt.value + suffix;
          opt.classList.toggle('is-unavailable', !matches.length);
          opt.disabled = !matches.length;
        });
      });
    };

    // When a change leaves an impossible combination, move the dropdowns below the
    // changed one to the closest real variant — preferring in-stock, then the one
    // that keeps the most of the shopper's other choices
    const resolveFrom = (index, selections) => {
      let best = null;
      let bestScore = -1;
      matchingPrefix(selections, index).forEach(v => {
        let score = v.available ? 100 : 0;
        v.options.forEach((opt, i) => {
          if (i >= index && opt === selections[`option${i + 1}`]) score += 1;
        });
        if (score > bestScore) { best = v; bestScore = score; }
      });
      if (!best) return null;
      $$('.variant-select').forEach(select => {
        const i = Number(select.dataset.optionIndex);
        if (i > index) select.value = best.options[i - 1];
      });
      return best;
    };

    const applyUnavailable = () => {
      // No variant exists for this combination — block the add instead of
      // silently submitting the previously selected variant
      variantInput.value = '';
      setBuyable(false, 'Unavailable');
      if (skuRow) skuRow.style.display = 'none';
      if (barcodeRow) barcodeRow.style.display = 'none';
    };

    const applyVariant = (variant) => {
      variantInput.value = variant.id;

      if (priceEl) priceEl.textContent = formatMoney(variant.price);
      if (comparePriceEl) {
        comparePriceEl.textContent = variant.compare_at_price ? formatMoney(variant.compare_at_price) : '';
        comparePriceEl.style.display = variant.compare_at_price ? 'inline' : 'none';
      }

      setBuyable(variant.available, 'Sold Out');

      if (skuValueEl && skuRow) {
        skuValueEl.textContent = variant.sku || '';
        skuRow.style.display = variant.sku ? '' : 'none';
      }
      if (barcodeValueEl && barcodeRow) {
        barcodeValueEl.textContent = variant.barcode || '';
        barcodeRow.style.display = variant.barcode ? '' : 'none';
      }

      const url = new URL(window.location);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url);

      if (variant.featured_image && window.__productGallery) {
        window.__productGallery.showImageById(variant.featured_image.id);
      }
    };

    $$('.variant-select').forEach(select => {
      on(select, 'change', () => {
        const selections = getCurrentSelections();
        const variant = findVariant(selections) ||
          resolveFrom(Number(select.dataset.optionIndex), selections);
        if (variant) applyVariant(variant);
        else applyUnavailable();
        updateOptionLabels();
      });
    });

    updateOptionLabels();
  };

  initVariants();

  // ============================================
  // PRODUCT TABS
  // ============================================
  const tabBtns = $$('.tab-btn');
  tabBtns.forEach(btn => {
    on(btn, 'click', () => {
      const target = btn.dataset.tab;
      const container = btn.closest('.product-tabs');
      $$('.tab-btn', container).forEach(b => b.classList.remove('active'));
      $$('.tab-panel', container).forEach(p => p.classList.remove('active'));
      btn.classList.add('active');
      $(`#tab-${target}`, container)?.classList.add('active');
    });
  });

  // ============================================
  // TAB DESCRIPTION SECTIONS (anchor-linkable tabs)
  // ============================================
  const activateTabDescriptionTab = (section, targetId) => {
    if (!targetId) return;
    const btn = section.querySelector(`.tab-btn[data-tab="${targetId}"]`);
    const panel = section.querySelector(`#${targetId}`);
    if (!btn || !panel) return;
    $$('.tab-btn', section).forEach(b => { b.classList.remove('active'); b.setAttribute('aria-selected', 'false'); });
    $$('.tab-panel', section).forEach(p => p.classList.remove('active'));
    btn.classList.add('active');
    btn.setAttribute('aria-selected', 'true');
    panel.classList.add('active');
  };

  $$('.tab-description').forEach(section => {
    $$('.tab-btn', section).forEach(btn => {
      on(btn, 'click', () => {
        activateTabDescriptionTab(section, btn.dataset.tab);
        if (btn.dataset.tab) history.replaceState(null, '', `#${btn.dataset.tab}`);
      });
    });

    if (location.hash) activateTabDescriptionTab(section, location.hash.slice(1));
  });

  on(window, 'hashchange', () => {
    const targetId = location.hash.slice(1);
    $$('.tab-description').forEach(section => activateTabDescriptionTab(section, targetId));
  });

  // ============================================
  // FILTER TOGGLES
  // ============================================
  $$('.filter-group__title').forEach(title => {
    on(title, 'click', () => {
      title.closest('.filter-group').classList.toggle('is-closed');
    });
  });

  // ============================================
  // COLLECTION SORT
  // ============================================
  const sortSelect = $('.sort-select');
  on(sortSelect, 'change', () => {
    const url = new URL(window.location);
    url.searchParams.set('sort_by', sortSelect.value);
    window.location = url;
  });

  // ============================================
  // MOBILE FILTER DRAWER
  // ============================================
  const filterToggleBtn = $('.filter-toggle-btn');
  const filtersPanel = $('.filters-panel');
  const filtersCloseBtn = $('.filters-panel__close');

  const openFiltersDrawer = () => {
    filtersPanel?.classList.add('is-mobile-open');
    overlay?.classList.add('is-active');
    document.body.style.overflow = 'hidden';
    filterToggleBtn?.setAttribute('aria-expanded', 'true');
  };

  const closeFiltersDrawer = () => {
    filtersPanel?.classList.remove('is-mobile-open');
    overlay?.classList.remove('is-active');
    document.body.style.overflow = '';
    filterToggleBtn?.setAttribute('aria-expanded', 'false');
  };

  on(filterToggleBtn, 'click', openFiltersDrawer);
  on(filtersCloseBtn, 'click', closeFiltersDrawer);
  on(overlay, 'click', closeFiltersDrawer);
  on(document, 'keydown', (e) => {
    if (e.key === 'Escape') closeFiltersDrawer();
  });

  // ============================================
  // CART PAGE QUANTITY
  // ============================================
  $$('.cart-table-item .quantity-btn').forEach(btn => {
    on(btn, 'click', async () => {
      const key = btn.dataset.key;
      const row = btn.closest('.cart-table-item');
      const input = row?.querySelector('.quantity-input');
      let qty = parseInt(input?.value || 0);
      btn.dataset.action === 'increase' ? qty++ : qty--;
      qty = Math.max(0, qty);

      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: qty })
        });
        window.location.reload();
      } catch (e) {
        showToast('Could not update cart', 'error');
      }
    });
  });

  // ============================================
  // CART PAGE REMOVE
  // ============================================
  $$('.cart-table-remove[data-key]').forEach(btn => {
    on(btn, 'click', async () => {
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: btn.dataset.key, quantity: 0 })
        });
        window.location.reload();
      } catch (e) {
        showToast('Could not remove item', 'error');
      }
    });
  });

  // ============================================
  // TOAST NOTIFICATIONS
  // ============================================
  let toastTimeout;
  const showToast = (message, type = 'success') => {
    let toast = $('.toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'toast';
      document.body.appendChild(toast);
    }

    const icon = type === 'success'
      ? '<svg class="toast__icon" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>'
      : '<svg class="toast__icon" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>';

    toast.className = `toast toast--${type}`;
    toast.innerHTML = icon + `<span>${message}</span>`;

    clearTimeout(toastTimeout);
    requestAnimationFrame(() => {
      toast.classList.add('is-show');
    });

    toastTimeout = setTimeout(() => {
      toast.classList.remove('is-show');
    }, 3500);
  };

  window.HAD = { showToast, openCartDrawer, closeCartDrawer, fetchCart };

  // ============================================
  // SLIDESHOW
  // ============================================
  const initSlideshows = () => {
    $$('.slideshow').forEach(initSlideshow);
  };

  const initSlideshow = (el) => {
    const slides     = $$('.slide', el);
    const dots       = $$('.slideshow__dot', el);
    const prevBtn    = $('[data-slideshow-prev]', el);
    const nextBtn    = $('[data-slideshow-next]', el);
    const progressBar = $('.slideshow__progress-bar', el);
    const transition  = el.dataset.transition || 'slide';
    const autoplay    = el.dataset.autoplay === 'true';
    const delay       = parseInt(el.dataset.delay) || 5000;

    // Apply transition attr for CSS targeting
    el.setAttribute('data-transition', transition);

    // Apply height attr for CSS targeting
    const heightSetting = el.closest('.hero-slideshow-section')?.dataset?.height;
    if (heightSetting) el.setAttribute('data-height', heightSetting);

    if (slides.length <= 1) return;

    let current = 0;
    let timer   = null;
    let isPaused = false;

    const goTo = (index, dir = 'next') => {
      if (index === current) return;

      const prev = current;
      current = (index + slides.length) % slides.length;

      // Handle slide transition leave class
      if (transition === 'slide') {
        slides[prev].classList.add('is-leaving');
        setTimeout(() => {
          slides[prev].classList.remove('is-leaving', 'is-active');
        }, 700);
      } else {
        slides[prev].classList.remove('is-active');
      }

      slides[current].classList.add('is-active');

      // Update dots
      dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === current);
        dot.setAttribute('aria-selected', i === current ? 'true' : 'false');
      });

      // Restart progress bar
      if (progressBar) {
        progressBar.style.animation = 'none';
        progressBar.offsetHeight;  // reflow
        progressBar.style.animationDuration = delay + 'ms';
        progressBar.style.animation = '';
      }
    };

    const next = () => goTo(current + 1, 'next');
    const prev = () => goTo(current - 1, 'prev');

    const startTimer = () => {
      if (!autoplay || isPaused) return;
      clearInterval(timer);
      timer = setInterval(next, delay);
    };

    const stopTimer  = () => clearInterval(timer);

    const pausePlay  = () => {
      isPaused = !isPaused;
      isPaused ? stopTimer() : startTimer();
    };

    // Bind arrows
    on(prevBtn, 'click', () => { stopTimer(); prev(); startTimer(); });
    on(nextBtn, 'click', () => { stopTimer(); next(); startTimer(); });

    // Bind dots
    dots.forEach(dot => {
      on(dot, 'click', () => {
        stopTimer();
        goTo(parseInt(dot.dataset.dot));
        startTimer();
      });
    });

    // Pause on hover
    on(el, 'mouseenter', () => { if (autoplay) stopTimer(); });
    on(el, 'mouseleave', () => { if (autoplay && !isPaused) startTimer(); });

    // Touch / swipe support
    let touchStartX = 0;
    let touchStartY = 0;
    on(el, 'touchstart', (e) => {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
    }, { passive: true });
    on(el, 'touchend', (e) => {
      const dx = e.changedTouches[0].clientX - touchStartX;
      const dy = e.changedTouches[0].clientY - touchStartY;
      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 40) {
        stopTimer();
        dx < 0 ? next() : prev();
        startTimer();
      }
    }, { passive: true });

    // Keyboard navigation when slideshow is focused
    on(el, 'keydown', (e) => {
      if (e.key === 'ArrowLeft')  { stopTimer(); prev(); startTimer(); }
      if (e.key === 'ArrowRight') { stopTimer(); next(); startTimer(); }
    });

    // Pause button (optional - added dynamically for accessibility)
    if (autoplay) {
      const pauseBtn = document.createElement('button');
      pauseBtn.className = 'slideshow__pause';
      pauseBtn.setAttribute('aria-label', 'Pause slideshow');
      pauseBtn.innerHTML = `<svg fill="currentColor" viewBox="0 0 24 24"><path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/></svg>`;
      el.appendChild(pauseBtn);
      on(pauseBtn, 'click', () => {
        pausePlay();
        pauseBtn.innerHTML = isPaused
          ? `<svg fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>`
          : `<svg fill="currentColor" viewBox="0 0 24 24"><path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/></svg>`;
        pauseBtn.setAttribute('aria-label', isPaused ? 'Play slideshow' : 'Pause slideshow');
      });
    }

    // Respect prefers-reduced-motion
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      el.setAttribute('data-transition', 'fade');
    }

    // Start
    startTimer();
  };

  initSlideshows();

  // ============================================
  // GALLERY LIGHTBOX
  // ============================================
  function initGalleryLightbox() {
    $$('[data-gallery-item]').forEach((section) => {
      const grid = section.closest('.gallery-grid-section') || document;
      const lightbox = grid.querySelector('.gallery-lightbox');
      if (!lightbox) return;
      const img = lightbox.querySelector('.gallery-lightbox__img');
      const closeBtn = lightbox.querySelector('.gallery-lightbox__close');
      const prevBtn = lightbox.querySelector('.gallery-lightbox__prev');
      const nextBtn = lightbox.querySelector('.gallery-lightbox__next');
      const items = [...(grid.querySelectorAll('[data-gallery-item]'))];
      let current = 0;

      function open(index) {
        current = index;
        const src = items[current].href || items[current].getAttribute('href');
        const alt = items[current].querySelector('img')?.alt || '';
        img.src = src;
        img.alt = alt;
        lightbox.hidden = false;
        lightbox.focus();
        document.body.style.overflow = 'hidden';
      }

      function close() {
        lightbox.hidden = true;
        document.body.style.overflow = '';
      }

      function navigate(dir) {
        current = (current + dir + items.length) % items.length;
        const src = items[current].href || items[current].getAttribute('href');
        const alt = items[current].querySelector('img')?.alt || '';
        img.src = src;
        img.alt = alt;
      }

      items.forEach((item, i) => {
        on(item, 'click', (e) => {
          e.preventDefault();
          open(i);
        });
      });

      on(closeBtn, 'click', close);
      on(prevBtn, 'click', () => navigate(-1));
      on(nextBtn, 'click', () => navigate(1));
      on(lightbox, 'click', (e) => { if (e.target === lightbox) close(); });
      on(document, 'keydown', (e) => {
        if (lightbox.hidden) return;
        if (e.key === 'Escape') close();
        if (e.key === 'ArrowLeft') navigate(-1);
        if (e.key === 'ArrowRight') navigate(1);
      });
    });
  }
  initGalleryLightbox();

  // ============================================
  // PRODUCT DESCRIPTION CAROUSEL
  // ============================================
  $$('[data-carousel]').forEach((carousel) => {
    const track = $('[data-carousel-track]', carousel);
    const prevBtn = $('[data-carousel-prev]', carousel);
    const nextBtn = $('[data-carousel-next]', carousel);
    if (!track) return;

    on(prevBtn, 'click', () => track.scrollBy({ left: -track.clientWidth, behavior: 'smooth' }));
    on(nextBtn, 'click', () => track.scrollBy({ left: track.clientWidth, behavior: 'smooth' }));
  });

  // ============================================
  // PRODUCT IMAGE ACCORDION
  // ============================================
  $$('[data-image-accordion]').forEach((wrap) => {
    const items = $$('[data-image-accordion-item]', wrap);
    const images = $$('[data-image-accordion-image]', wrap);

    items.forEach((item) => {
      const toggle = $('[data-image-accordion-toggle]', item);
      on(toggle, 'click', () => {
        if (item.classList.contains('active')) return;
        const index = item.dataset.imageAccordionIndex;

        items.forEach((i) => {
          i.classList.remove('active');
          $('[data-image-accordion-toggle]', i)?.setAttribute('aria-expanded', 'false');
        });
        item.classList.add('active');
        toggle.setAttribute('aria-expanded', 'true');

        images.forEach((img) => {
          img.classList.toggle('active', img.dataset.imageAccordionIndex === index);
        });
      });
    });
  });

  // ============================================
  // ANNOUNCEMENT BAR CLOSE
  // ============================================
  on($('.announcement-bar__close'), 'click', (e) => {
    e.target.closest('.announcement-bar')?.remove();
  });

  // ============================================
  // SCROLL TO TOP
  // ============================================
  const scrollTopBtn = document.getElementById('scroll-to-top');
  if (scrollTopBtn) {
    window.addEventListener('scroll', () => {
      if (window.scrollY > 400) {
        scrollTopBtn.removeAttribute('hidden');
      } else {
        scrollTopBtn.setAttribute('hidden', '');
      }
    }, { passive: true });
    on(scrollTopBtn, 'click', () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  // ============================================
  // INIT CART COUNT ON LOAD
  // ============================================
  fetchCart();

})();
